const TD_VEHICLE_STORAGE_KEY = 'td-saved-vehicles';
const TD_VEHICLE_RESULTS_PATH = '/pages/year-make-product-search-results';
const TD_VEHICLE_PARAM_PATTERNS = {
  make: /^[a-z]{2,12}$/,
  year: /^\d{4}$/,
  engine: /^[A-Za-z0-9-]{1,12}$/,
  body: /^[A-Za-z]{1,12}$/,
};

// Some browsers refuse localStorage (site data blocked). Vehicles then live here for the
// current page only, and the results URL carries the vehicle from page to page.
let tdMemoryVehicles = null;

function generateVehicleId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `vehicle-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getSavedVehicles() {
  // Copies, so callers can mutate the result before saving like they do with parsed JSON.
  if (tdMemoryVehicles) return tdMemoryVehicles.map((vehicle) => ({ ...vehicle }));

  try {
    const raw = localStorage.getItem(TD_VEHICLE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveVehicles(vehicles) {
  try {
    localStorage.setItem(TD_VEHICLE_STORAGE_KEY, JSON.stringify(vehicles));
    tdMemoryVehicles = null;
  } catch (error) {
    console.warn('[TD Vehicle] localStorage unavailable, keeping vehicle for this page only.', error);
    tdMemoryVehicles = vehicles.map((vehicle) => ({ ...vehicle }));
  }
  window.dispatchEvent(new CustomEvent('td:vehicle-storage-change'));
}

function vehicleFromSearchParams(search) {
  const params = new URLSearchParams(search);
  const vehicle = {
    make: params.get('make'),
    year: params.get('year'),
    engine: params.get('engine'),
    body: params.get('body') || 'all',
  };

  const isValid = Object.entries(TD_VEHICLE_PARAM_PATTERNS).every(([key, pattern]) =>
    pattern.test(vehicle[key] || '')
  );
  return isValid ? vehicle : null;
}

function getResultsUrl(vehicle = getSelectedVehicle()) {
  if (!vehicle?.make) return TD_VEHICLE_RESULTS_PATH;

  const params = new URLSearchParams({
    make: vehicle.make,
    year: vehicle.year,
    engine: vehicle.engine,
    body: vehicle.body || 'all',
  });
  return `${TD_VEHICLE_RESULTS_PATH}?${params}`;
}

function vehiclesMatch(vehicle, { make, year, engine, body }) {
  return (
    vehicle.make === make &&
    vehicle.year === year &&
    vehicle.engine === engine &&
    vehicle.body === body
  );
}

function addVehicle({ make, year, engine, body }) {
  const vehicles = getSavedVehicles();
  const existingVehicle = vehicles.find((vehicle) =>
    vehiclesMatch(vehicle, { make, year, engine, body })
  );

  if (existingVehicle) {
    vehicles.forEach((vehicle) => {
      vehicle.selected = vehicle.id === existingVehicle.id;
    });
    saveVehicles(vehicles);
    return existingVehicle;
  }

  vehicles.forEach((vehicle) => {
    vehicle.selected = false;
  });

  const newVehicle = {
    id: generateVehicleId(),
    make,
    year,
    engine,
    body,
    selected: true,
  };

  vehicles.push(newVehicle);
  saveVehicles(vehicles);

  return newVehicle;
}

function formatEngineDisplay(engine) {
  if (!engine) return '';
  return String(engine).replace(/-/g, '.');
}

function getSelectedVehicle() {
  return getSavedVehicles().find((vehicle) => vehicle.selected) || null;
}

function hasSelectedVehicle() {
  return Boolean(getSelectedVehicle()?.make);
}

function selectVehicle(id) {
  const vehicles = getSavedVehicles();
  const targetVehicle = vehicles.find((vehicle) => vehicle.id === id);
  if (!targetVehicle) return null;

  vehicles.forEach((vehicle) => {
    vehicle.selected = vehicle.id === id;
  });
  saveVehicles(vehicles);

  return targetVehicle;
}

function removeVehicle(id) {
  const vehicles = getSavedVehicles();
  const removedVehicle = vehicles.find((vehicle) => vehicle.id === id);
  if (!removedVehicle) return null;

  const remainingVehicles = vehicles.filter((vehicle) => vehicle.id !== id);
  saveVehicles(remainingVehicles);

  return removedVehicle;
}

function clearSelectedVehicle() {
  const vehicles = getSavedVehicles();
  if (!vehicles.some((vehicle) => vehicle.selected)) return false;

  vehicles.forEach((vehicle) => {
    vehicle.selected = false;
  });
  saveVehicles(vehicles);

  return true;
}

window.TdVehicleStorage = {
  STORAGE_KEY: TD_VEHICLE_STORAGE_KEY,
  RESULTS_PATH: TD_VEHICLE_RESULTS_PATH,
  getResultsUrl,
  vehicleFromSearchParams,
  getSavedVehicles,
  saveVehicles,
  addVehicle,
  getSelectedVehicle,
  hasSelectedVehicle,
  selectVehicle,
  removeVehicle,
  clearSelectedVehicle,
  formatEngineDisplay,
};
